import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { Doc, Id } from "@/convex/_generated/dataModel";
import { api } from "@/convex/_generated/api";
import { createTableHook, TableProps } from "@/hooks/useTableFactory";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import UsersModal from "@/components/users/users-modal";
import { useToastMutation } from "@/hooks/useToastMutation";
import { Mail, ArrowLeftRight } from "lucide-react";
import { toast } from "sonner";
import { useState } from "react";
import { useAuthActions } from "@convex-dev/auth/react";
import { UserReassignDialog } from "@/components/users/user-reassign-dialog";

const usersSearchSchema = z.object({
  page: z.number().optional().default(1),
  search: z.string().optional(),
  status: z.string().optional(),
});

export const Route = createFileRoute("/_super/super/users")({
  validateSearch: usersSearchSchema,
  component: UsersPage,
});

type UserWithRole = Doc<"users"> & {
  role?: Doc<"roles"> | null;
  company?: Doc<"companies"> | null;
};

const useUsersTable = createTableHook<UserWithRole>({
  tableName: "users",
  query: api.system.users.queries.getUsersOptimizedForSuperAdmin,
  title: "Usuarios",
  description: "Lista de usuarios del sistema",
  createAction: "modal",
  enableRowSelection: true,

  columns: () => [
    {
      sortable: true,
      key: "name",
      label: "Usuario",
      required: true,
      render: (value, row) => (
        <div>
          <div className="font-medium">{value}</div>
          <div className="text-sm text-muted-foreground">{row.email}</div>
        </div>
      ),
    },
    {
      sortable: true,
      key: "role",
      label: "Rol",
      render: (value: Doc<"roles"> | null | undefined) => {
        if (!value) return <Badge variant="secondary">Sin rol</Badge>;

        const variants: Record<
          string,
          "default" | "secondary" | "destructive"
        > = {
          super_admin: "destructive",
          company_admin: "default",
          company_user: "secondary",
        };

        return (
          <Badge variant={variants[value.type] || "secondary"}>
            {value.name}
          </Badge>
        );
      },
    },
    {
      sortable: true,
      key: "phone",
      label: "Teléfono",
      render: (value) => (
        <div className="text-sm text-muted-foreground">{value || "-"}</div>
      ),
    },
    {
      sortable: true,
      key: "internalControl",
      label: "Estado",
      render: (value: any) => {
        const state = value?.state || "inactive";
        const variants: Record<
          string,
          "default" | "secondary" | "destructive"
        > = {
          active: "default",
          inactive: "secondary",
          deleted: "destructive",
        };
        const labels: Record<string, string> = {
          active: "Activo",
          inactive: "Inactivo",
          deleted: "Eliminado",
        };
        return (
          <Badge variant={variants[state] || "secondary"}>
            {labels[state] || state}
          </Badge>
        );
      },
    },
    {
      key: "company",
      label: "Empresa",
      render: (value: Doc<"companies"> | null) => (
        <div className="text-sm">
          {value?.name || (
            <span className="text-muted-foreground">Sin empresa</span>
          )}
        </div>
      ),
    },
    {
      sortable: true,
      key: "_creationTime",
      label: "Creado",
      render: (value: number) => {
        const date = new Date(value);
        return (
          <div className="text-sm text-muted-foreground">
            {format(date, "dd MMM yyyy", { locale: es })}
          </div>
        );
      },
    },
  ],

  filters: () => [
    {
      id: "state",
      name: "Estado",
      icon: "Activity",
      options: [
        { value: "all", label: "Todos" },
        { value: "active", label: "Activos" },
        { value: "inactive", label: "Inactivos" },
      ],
      defaultValue: "all",
    },
  ],

  customHooks: () => {
    const [isActionLoading, setIsActionLoading] = useState(false);
    const [reassignTarget, setReassignTarget] = useState<UserWithRole | null>(
      null,
    );
    const { signIn } = useAuthActions();

    const handleSendCode = async (user: UserWithRole) => {
      if (!user.email) {
        toast.error("El usuario no tiene email configurado");
        return;
      }

      setIsActionLoading(true);
      try {
        await signIn("resend-otp", {
          email: user.email,
        });
        toast.success("Código OTP enviado exitosamente", {
          description: `Se envió un código a ${user.email}`,
        });
      } catch (error) {
        toast.error("Error al enviar código OTP", {
          description:
            error instanceof Error ? error.message : "Error desconocido",
        });
      } finally {
        setIsActionLoading(false);
      }
    };

    return {
      isActionLoading,
      reassignTarget,
      setReassignTarget,
      handleSendCode,
    };
  },

  actions: (helpers, customHooksData: Record<string, any>) => {
    const { handleSendCode, isActionLoading, setReassignTarget } =
      customHooksData;

    const { execute: deleteUser, bar: deleteUserBar } = useToastMutation(
      api.system.users.mutations.deleteUser,
      {
        loadingMessage: "Eliminando usuario...",
        successMessage: "Usuario eliminado",
        errorMessage: (error) => `Error al eliminar usuario: ${error}`,
      },
    );

    return {
      bars: [deleteUserBar],
      onEdit: (user) => {
        helpers.modal.edit(user);
      },
      // El factory ya renderiza un DeleteConfirmationModal con confirmacion
      // critica (escribir el nombre del item) cuando onDelete devuelve void.
      // Si devolvemos una Promise (caso skipModal), el factory tambien la
      // espera. La mutacion real se dispara desde el modal; aqui solo lo
      // abrimos con critical=true para forzar la confirmacion explicita.
      onDelete: async (user, opts) => {
        if (opts?.skipModal) {
          await deleteUser({ id: user._id as Id<"users"> });
          return;
        }
        helpers.deleteModal.open(user, true);
      },
      customActions: [
        {
          label: "Enviar Codigo OTP",
          icon: Mail,
          onClick: (user: UserWithRole) => handleSendCode(user),
          disabled: isActionLoading,
          hidden: (user: UserWithRole) =>
            !user.email || user.internalControl?.state !== "active",
        },
        {
          label: "Reasignar Empresa",
          icon: ArrowLeftRight,
          onClick: (user: UserWithRole) => setReassignTarget(user),
          hidden: (user: UserWithRole) =>
            user.internalControl?.state !== "active",
        },
      ],
    };
  },

  modal: {
    component: UsersModal,
    getItemName: (user) => user?.name || "Usuario",
  },
});

function UsersPage() {
  const searchParams = Route.useSearch();

  return (
    <UsersTable
      currentPage={searchParams.page}
      basePath="/super/users"
      searchParams={searchParams}
    />
  );
}

export default function UsersTable({
  currentPage,
  pageSize,
  basePath,
  searchParams,
}: TableProps) {
  const table = useUsersTable({
    currentPage,
    pageSize,
    basePath,
    searchParams,
  });

  const { reassignTarget, setReassignTarget } =
    (table.customHooksData as Record<string, any>) || {};

  return (
    <>
      {table.render()}
      {reassignTarget && (
        <UserReassignDialog
          isOpen={!!reassignTarget}
          onClose={() => setReassignTarget?.(null)}
          user={reassignTarget}
          currentCompanyId={reassignTarget.internalControl?.companyId}
          onSuccess={() => setReassignTarget?.(null)}
        />
      )}
    </>
  );
}
